import axiosInstance from "../config/axiosInstance";
import { ApiRoutes } from "../constants/ApiRoutes";
import { QueryKeys } from "../constants/QueryKeys";
import { MutateKeys } from "../constants/MutateKeys";
import { useMutation, useQuery } from "@tanstack/react-query";
import { generatePath } from "react-router-dom";
import { lienFilterId } from "../enums/lienProcessingEnums";
import {
  LienDashboardPayloadType,
  LienDetailViewPayloadType,
  lienFilterPayloadType,
} from "../Types/lienProcessingTypes";
import { cleanParams } from "../Utilities/queryParamUtils";
import { filterEmptyValues } from "../utils/GetFilteredPayload";

const LienProcessingService = () => {
  const {
    FETCH_LIEN_DASHBOARD,
    FETCH_LIEN_FILTERS,
    FETCH_LIEN_DETAILS_ROUTE,
    FETCH_LIEN_BATCH_DOCUMENTS_ROUTE,
    FETCH_LIEN_BATCH_DOCUMENTS_ZIP_ROUTE,
    SYNC_WITH_QUICKBOOKS,
    GENERATE_RECOMMENDED_DOCUMENTS,
    DOWNLOAD_ALL_LIEN,
    EXPORT_NON_SYNCED,
    DOWNLOAD_SINGLE_BATCH,
  } = ApiRoutes;

  const {
    FETCH_LIEN_DASHBOARD_KEY,
    FETCH_LIEN_DETAILS_KEY,
    FETCH_BATCH_DOCUMENTS_KEY,
    FETCH_BATCH_DOCUMENTS_ZIP_KEY,
    FETCH_LIEN_CUSTOMER_FILTERS_KEY,
    FETCH_LIEN_JOB_FILTERS_KEY,
    FETCH_LIEN_MONTH_FILTERS_KEY,
  } = QueryKeys;

  const {
    SYNC_WITH_QUICKBOOKS_KEY,
    DOWNLOAD_ALL_LIEN_KEY,
    EXPORT_NON_SYNCED_KEY,
    DOWNLOAD_SINGLE_BATCH_KEY,
  } = MutateKeys;

  const FetchLienProcessDashboard = (
    payload: LienDashboardPayloadType,
    pageSizeChanged: boolean,
    enabledFlag: boolean,
  ) => {
    const fetchLienProcessDashboard = async () => {
      const { data } = await axiosInstance.post(FETCH_LIEN_DASHBOARD, {
          ...filterEmptyValues({
            ...payload,
            setDefaultPageSize: pageSizeChanged,
          })
        },
      );
      return data;
    };

    return useQuery({
      queryKey: [FETCH_LIEN_DASHBOARD_KEY, payload],
      queryFn: fetchLienProcessDashboard,
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      enabled: !!enabledFlag && !!payload?.perPage,
    });
  };

  const FetchLienCustomerFilterMeta = (payloadData: lienFilterPayloadType) => {
    const fetchLienCustomerOptions = async () => {
      const payload = {
        ...filterEmptyValues(payloadData),
      };

      const url = generatePath(FETCH_LIEN_FILTERS, {
        filterId: lienFilterId.CUSTOMER_FILTER_ID,
      });

      const { data } = await axiosInstance.post(url, payload);

      return data;
    };

    return useQuery({
      queryKey: [FETCH_LIEN_CUSTOMER_FILTERS_KEY],
      queryFn: fetchLienCustomerOptions,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    });
  };

  const FetchLienJobFilterMeta = (payloadData: lienFilterPayloadType) => {
    const fetchLienJobOptions = async () => {
      const payload = {
        ...filterEmptyValues(payloadData),
      };

      const url = generatePath(FETCH_LIEN_FILTERS, {
        filterId: lienFilterId.JOB_FILTER_ID,
      });

      const { data } = await axiosInstance.post(url, payload);

      return data;
    };

    return useQuery({
      queryKey: [FETCH_LIEN_JOB_FILTERS_KEY],
      queryFn: fetchLienJobOptions,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    });
  };

  const FetchLienMonthFilterMeta = (payloadData: lienFilterPayloadType) => {
    const fetchLienMonthOptions = async () => {
      const payload = {
        ...filterEmptyValues(payloadData),
      };

      const url = generatePath(FETCH_LIEN_FILTERS, {
        filterId: lienFilterId.MONTH_FILTER_ID,
      });

      const { data } = await axiosInstance.post(url, payload);

      return data;
    };

    return useQuery({
      queryKey: [FETCH_LIEN_MONTH_FILTERS_KEY],
      queryFn: fetchLienMonthOptions,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    });
  };

  const SyncWithQuickbooks = () => {
    const syncWithQuickbooks = async () => {
      const { data } = await axiosInstance.get(SYNC_WITH_QUICKBOOKS);
      return data;
    };

    return useMutation({
      mutationFn: syncWithQuickbooks,
      mutationKey: [SYNC_WITH_QUICKBOOKS_KEY],
    });
  };

  const GenerateRecommendedDocuments = () => {
    const generateRecommendedDocuments = async (batchId: string) => {
      const payload = {
        batchId: batchId,
      };
      const { data } = await axiosInstance.post(GENERATE_RECOMMENDED_DOCUMENTS, payload);
      return data;
    };

    return useMutation({
      mutationFn: generateRecommendedDocuments,
      mutationKey: [GENERATE_RECOMMENDED_DOCUMENTS],
    });
  };

  const DownloadAllLienDocuments = () => {
    const downloadAllLienDocuments = async (
      payloadData: any[],
    ) => {
      const payload = {
        ...filterEmptyValues(payloadData),
      };
      const { data } = await axiosInstance.post(DOWNLOAD_ALL_LIEN, payload);
      return data;
    };

    return useMutation({
      mutationFn: downloadAllLienDocuments,
      mutationKey: [DOWNLOAD_ALL_LIEN_KEY],
    });
  };

  const ExportNonSyncedRecords = () => {
    const exportNonSyncedRecords = async () => {
      const { data } = await axiosInstance.get(EXPORT_NON_SYNCED);
      return data;
    };

    return useMutation({
      mutationFn: exportNonSyncedRecords,
      mutationKey: [EXPORT_NON_SYNCED_KEY],
    });
  };

  const DownloadSingleEntryBatch = () => {
    const downloadSingleEntryBatch = async (batchId: string) => {
      const url = generatePath(DOWNLOAD_SINGLE_BATCH, { batchId: batchId });
      const { data } = await axiosInstance.get(url);
      return data;
    };

    return useMutation({
      mutationFn: downloadSingleEntryBatch,
      mutationKey: [DOWNLOAD_SINGLE_BATCH_KEY],
    });
  };

  const FetchLienDetailViewData = (payload?: LienDetailViewPayloadType) => {
    const fetchLienDetailViewData = async () => {
      const { groupId, ...rest } = payload;
      const url = generatePath(FETCH_LIEN_DETAILS_ROUTE, {
        groupId: groupId,
      });
      const cleanedParams = cleanParams(rest);
      const data = await axiosInstance.get(url, { params: cleanedParams });
      return data;
    };
    return useQuery({
      queryKey: [FETCH_LIEN_DETAILS_KEY],
      queryFn: fetchLienDetailViewData,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      enabled: !!payload?.groupId,
    });
  };

  const FetchBatchDocuments = (batchId?: string, enabled: boolean) => {
    const fetchBatchDocuments = async () => {
      const url = generatePath(FETCH_LIEN_BATCH_DOCUMENTS_ROUTE, {
        batchId: batchId,
      });
      const data = await axiosInstance.get(url);
      return data;
    };
    return useQuery({
      queryKey: [FETCH_BATCH_DOCUMENTS_KEY, batchId],
      queryFn: fetchBatchDocuments,
      enabled: !!batchId && !!enabled,
      retry: false,
      refetchOnWindowFocus: false,
    });
  };

  const FetchBatchDocumentsZip = (batchId?: string, enabled: boolean) => {
    const fetchBatchDocumentsZip = async () => {
      const url = generatePath(FETCH_LIEN_BATCH_DOCUMENTS_ZIP_ROUTE, {
        batchId: batchId,
      });
      const data = await axiosInstance.get(url);
      return data;
    };
    return useQuery({
      queryKey: [FETCH_BATCH_DOCUMENTS_ZIP_KEY],
      queryFn: fetchBatchDocumentsZip,
      enabled: !!batchId && !!enabled,
      retry: false,
    });
  };

  return {
    SyncWithQuickbooks,
    DownloadAllLienDocuments,
    ExportNonSyncedRecords,
    GenerateRecommendedDocuments,
    FetchLienProcessDashboard,
    FetchLienCustomerFilterMeta,
    FetchLienJobFilterMeta,
    FetchLienMonthFilterMeta,
    FetchLienDetailViewData,
    FetchBatchDocuments,
    FetchBatchDocumentsZip,
    DownloadSingleEntryBatch,
  };
};

export default LienProcessingService;
